export interface ToleranceSettings {
    tempControllerToleranceC: number;
    speedTolerancePct: number;
    screwRpmTolerancePct: number;
    ovenZoneToleranceC: number;
    mwPowerToleranceKw: number;
}

// 'absolute' — toleranceValue is added/subtracted directly from the setpoint (right for °C and
// KW categories, where the number is a flat physical offset). 'percent' — toleranceValue is a
// percentage OF the setpoint's own value at each point, needed for Puller speed (m/min) where
// "±0.1%" means a band that scales with the current setpoint, not a flat 0.1 m/min.
export type ToleranceMode = 'absolute' | 'percent';

export interface CategoryRule {
    matchDescription: RegExp;
    matchUnit: string;
    settingsKey: keyof ToleranceSettings;
    toleranceMode: ToleranceMode;
}

export interface ResolvedTolerance {
    value: number;
    mode: ToleranceMode;
}

// The seed defaults below are the offline fallback. LT-3221 makes db_api the source of
// truth: setToleranceSource() hydrates the active rules/settings from the facility-scoped
// chart_tolerance_settings row (fetched once per facility load), so console and mobile stop
// drawing different bands. These constants match that seed so the fallback agrees too.
export const DEFAULT_CATEGORY_RULES: CategoryRule[] = [
    {
        matchDescription: /barrel|head|die|screw|feed|adapter|hose|clamp/i,
        matchUnit: '°C',
        settingsKey: 'tempControllerToleranceC',
        toleranceMode: 'absolute',
    },
    // Line speeds (puller/conveyor/preformer/postformer): the real unit is m/min, but the
    // tolerance IS a percentage applied proportionally to the setpoint, not a flat m/min offset.
    {
        matchDescription: /puller|conveyor|preform|postform/i,
        matchUnit: 'm/min',
        settingsKey: 'speedTolerancePct',
        toleranceMode: 'percent',
    },
    // Screw drive holds tighter than line speed and is separated from the temperature rule by
    // its unit: a "Screw Zone" in °C is a heater, "Screw rpm" is the drive.
    {
        matchDescription: /screw/i,
        matchUnit: 'rpm',
        settingsKey: 'screwRpmTolerancePct',
        toleranceMode: 'percent',
    },
    // Grid row labels for oven zones are abbreviated (e.g. "HotAirCh1"/"HotAirBn1" for
    // Channel/Burner) — match the shared "HotAir" prefix rather than the abbreviated letters,
    // which would be too ambiguous as standalone substrings.
    {
        matchDescription: /oven|burner|channel|hotair/i,
        matchUnit: '°C',
        settingsKey: 'ovenZoneToleranceC',
        toleranceMode: 'absolute',
    },
    { matchDescription: /mw|microwave/i, matchUnit: 'KW', settingsKey: 'mwPowerToleranceKw', toleranceMode: 'absolute' },
];

export const DEFAULT_TOLERANCE_SETTINGS: ToleranceSettings = {
    tempControllerToleranceC: 2,
    speedTolerancePct: 0.1,
    screwRpmTolerancePct: 0.1,
    ovenZoneToleranceC: 10,
    mwPowerToleranceKw: 3,
};

// The raw shape served by db_api (snake_case, match_description as a string). Kept separate
// from the compiled CategoryRule so the case-insensitivity flag is applied here, once, rather
// than baked into the stored pattern (which would not port between Python re and JS RegExp).
export interface ServerCategoryRule {
    match_description: string;
    match_unit: string;
    settings_key: string;
    mode: ToleranceMode;
}
export interface ServerToleranceData {
    settings?: Partial<ToleranceSettings>;
    rules?: ServerCategoryRule[];
}

// Active source, hydrated from the server. Holds the seed until setToleranceSource() runs (or
// if the fetch fails), so charts always have a band source and never regress to "no band".
let activeCategoryRules: CategoryRule[] = DEFAULT_CATEGORY_RULES;
let activeToleranceSettings: ToleranceSettings = DEFAULT_TOLERANCE_SETTINGS;

// Hydrate the active rules/settings from a fetched chart_tolerance_settings data blob. Compiles
// each match_description case-insensitively. On any bad rule the whole set is left unchanged
// (db_api validates on write, but never let a malformed payload blank every band client-side).
export const setToleranceSource = (data: ServerToleranceData | null | undefined): void => {
    if (!data) return;
    // All-or-nothing: compute the next settings AND rules, then commit both together. A
    // malformed rule abandons the whole hydration, so settings and rules never disagree.
    let nextSettings = activeToleranceSettings;
    let nextRules = activeCategoryRules;
    if (data.settings) {
        nextSettings = { ...DEFAULT_TOLERANCE_SETTINGS, ...data.settings };
    }
    if (data.rules && data.rules.length) {
        try {
            nextRules = data.rules.map((r) => ({
                matchDescription: new RegExp(r.match_description, 'i'),
                matchUnit: r.match_unit,
                settingsKey: r.settings_key as keyof ToleranceSettings,
                toleranceMode: r.mode,
            }));
        } catch {
            // A malformed rule must not remove all bands, nor apply half the update.
            return;
        }
    }
    activeToleranceSettings = nextSettings;
    activeCategoryRules = nextRules;
};

// Restore the built-in seed (e.g. on logout or when switching to a facility with no row).
export const resetToleranceSource = (): void => {
    activeCategoryRules = DEFAULT_CATEGORY_RULES;
    activeToleranceSettings = DEFAULT_TOLERANCE_SETTINGS;
};

// LT-3415 phase 1 — the SERVED answer, keyed by equipment_data_tag_id.
//
// db_api now resolves the band itself and serves it per tag, because three consumers each
// reimplementing the walk below is why they can disagree — and do: mobile refuses a
// zero-width band where this file's callers draw both spec lines on the setpoint. When a
// map is loaded for the current cell we read it and stop resolving; when it is absent
// (older server, failed fetch, a cell db_api could not resolve) we fall back to the rules
// below, unchanged. That fallback is what makes serving the value non-breaking, and it is
// removed one release later.
//
// Three states, and they are all different:
//   band object  — this tag has this band
//   null entry   — this tag was resolved and has NO band; draw nothing
//   absent key   — db_api has no opinion on this tag; resolve it locally
export interface ServerResolvedTolerance {
    value: number;
    mode: ToleranceMode;
    /** the rule that produced it, e.g. "rule:temp_controller" — provenance, not a key */
    source?: string;
}
export type ServerResolvedToleranceMap = Record<string, ServerResolvedTolerance | null>;

let resolvedByTagId: ServerResolvedToleranceMap | null = null;

// Hydrate the served map for the cell being viewed. Pass null/undefined to clear it, which
// every caller must do when the cell changes — a map from the PREVIOUS cell would answer
// confidently for tag ids that are not on screen.
export const setResolvedTolerances = (map: ServerResolvedToleranceMap | null | undefined): void => {
    resolvedByTagId = map && Object.keys(map).length ? map : null;
};

export const clearResolvedTolerances = (): void => {
    resolvedByTagId = null;
};

// Exported for tests and for anything that needs to know whether a band is server-owned.
export const hasResolvedTolerances = (): boolean => resolvedByTagId !== null;

// First-match-wins walk of `rules` against a row's equipment description/label and unit.
// Returns null when nothing matches (e.g. amps/pressure rows) rather than a fallback number,
// so callers can distinguish "no spec band applies" from "band is zero". Defaults read the
// hydrated active source at call time, so existing call sites need no change.
//
// LT-3415: this is now the FALLBACK path — prefer resolveToleranceForTag().
export const resolveTolerance = (
    equipmentDescription: string,
    unit: string,
    rules: CategoryRule[] = activeCategoryRules,
    settings: ToleranceSettings = activeToleranceSettings,
): ResolvedTolerance | null => {
    const rule = rules.find((r) => r.matchDescription.test(equipmentDescription) && r.matchUnit === unit);
    if (!rule) return null;
    const value = settings[rule.settingsKey];
    if (typeof value !== 'number' || !Number.isFinite(value)) return null;
    return { value, mode: rule.toleranceMode };
};

// The half-width of the spec band around one setpoint, or null when there is no usable band.
//
// The ONE definition of that, because a band drawn on a chart and a band quoted in a stats
// popover disagreeing is exactly the class of bug LT-3415 exists to remove — and this file
// already had it across two repos before it had it across two components.
//
// A tolerance of 0 is finite, so a "is it a number" test admits it and yields a ZERO-WIDTH
// band sitting on the setpoint, against which every reading but an exact match is out of
// spec. Worse, it happens without anyone configuring it: a percent band is
// |setpoint| * value/100, which collapses whenever the SETPOINT is zero — an idle drive, a
// controller at rest. There is no true statement to make about a stopped machine's
// conformance, so there is no band. Mobile has refused this since it shipped
// (ll_console_mobile/src/lib/tolerance.ts `if (!(delta > 0)) return null;`); this is the
// console catching up.
export const specBandDelta = (
    setpoint: number | null | undefined,
    tolerance: ResolvedTolerance | null | undefined,
): number | null => {
    if (!tolerance || typeof setpoint !== 'number' || !Number.isFinite(setpoint)) return null;
    const delta =
        tolerance.mode === 'percent' ? Math.abs(setpoint) * (tolerance.value / 100) : tolerance.value;
    return Number.isFinite(delta) && delta > 0 ? delta : null;
};

// The band for one tag: the served answer when db_api has one, else the local walk above.
//
// Prefer this over resolveTolerance() at every call site. The tag id is the whole point —
// it makes the answer independent of how the equipment happens to be named, which is the
// direction the rest of LT-3415 takes the rules themselves.
export const resolveToleranceForTag = (
    equipmentDataTagId: number | string | null | undefined,
    equipmentDescription: string,
    unit: string,
): ResolvedTolerance | null => {
    if (resolvedByTagId !== null && equipmentDataTagId !== null && equipmentDataTagId !== undefined) {
        const served = resolvedByTagId[String(equipmentDataTagId)];
        // A null ENTRY is an answer — "no band" — and must not fall through to the local
        // rules, or the server's verdict is silently overridden by the one it replaced.
        if (served !== undefined) {
            return served === null ? null : { value: served.value, mode: served.mode };
        }
    }
    return resolveTolerance(equipmentDescription, unit);
};
